// src/pages/Payment.jsx
import React, { useState, useEffect } from "react";
import { generateOrder, verifyAndPlace, checkoutOrder } from "../api/api";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import "../styles/Payment.css";

export default function Payment({ userId }) {
  const navigate = useNavigate();
  const { clearCartLocally } = useCart();
  const [loading, setLoading]       = useState(true);
  const [processing, setProcessing] = useState(false);
  const [orderData, setOrderData]   = useState(null);
  const RAZOR_KEY                    = process.env.REACT_APP_RAZORPAY_KEY_ID;
  const tableInfo                    = JSON.parse(localStorage.getItem("tableInfo") || "{}");

  // Only needed for outdoor (non-QR) orders:
  const [deliveryData, setDeliveryData] = useState({
    type:    "delivery",
    phone:   "",
    address: ""
  });

  // 1️⃣ If it's an outdoor order, we need to create a Razorpay order.
  useEffect(() => {
    if (!userId) return navigate("/login");
    
    const currentTableInfo = JSON.parse(localStorage.getItem("tableInfo") || "{}");
    if (currentTableInfo.table) {
      // QR/indoor: no Razorpay order needed
      setLoading(false);
      return;
    }

    generateOrder({ user_id: userId })
      .then((res) => {
        if (res.data.error) {
          alert(res.data.error === "Cart is empty"
            ? "Your cart is empty."
            : res.data.error
          );
          return navigate("/");
        }
        // pull the raw payload out so we can log it if something goes wrong
       const payload = res.data;
       const order_id = payload.order_id;
       const amount   = Number(payload.amount);
       const currency = payload.currency;

        if (!order_id || typeof amount !== "number" || !currency) {
          console.error("Bad orderData fields:", payload);
          throw new Error("Bad order data");
        }
        setOrderData({ order_id, amount, currency });
      })
      .catch((err) => {
        console.error(err);
        alert("Could not start payment: " + err.message);
        navigate("/");
      })
      .finally(() => setLoading(false));
  }, [userId, navigate]);

  // 2️⃣ Render loading or error states
  if (loading) {
    return <div className="payment-container"><p>Initializing payment…</p></div>;
  }

  // 3️⃣ Pay-at-Counter handler (QR/indoor)
  const handlePayAtCounter = async () => {
    setProcessing(true);
    try {
      const payload = {
        user_id:      userId,
        table_number: tableInfo.table,
        order_type:   tableInfo.type
      };
      const res = await checkoutOrder(payload);
      if (res.data.error) throw new Error(res.data.error);
      alert(res.data.message);
      localStorage.removeItem("tableInfo");
      clearCartLocally();
      navigate("/orders");
    } catch (err) {
      console.error(err);
      alert("Order failed: " + err.message);
    } finally {
      setProcessing(false);
    }
  };

  // 4️⃣ Razorpay checkout opener (outdoor orders)
  const openRazorpay = () => {
    // validation
    if (!deliveryData.phone.trim()) {
      return alert("Please enter your phone number.");
    }
    if (deliveryData.type === "delivery" && !deliveryData.address.trim()) {
      return alert("Please enter your delivery address.");
    }

    setProcessing(true);
    new window.Razorpay({
      key:       RAZOR_KEY,
      amount:    orderData.amount,
      currency:  orderData.currency,
      order_id:  orderData.order_id,
      name:      "Food Delivery",
      description: "Online order",
      handler(response) {
        verifyAndPlace({
          user_id:             userId,
          razorpay_order_id:   response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature:  response.razorpay_signature,
          delivery_address:    deliveryData.address,
          contact_phone:       deliveryData.phone,
          // Add these so backend can read them from JSON
          // Changed to send flat data
          table_number:        0,
          order_type:          deliveryData.type
        })
          .then((res) => {
            if (res.data.error) throw new Error(res.data.error);
            if (!res.data.message) throw new Error("Empty response from server"); // Catch silent failures
            alert(res.data.message);
            clearCartLocally();
            navigate("/orders");
          })
          .catch((e) => {
            console.error(e);
            alert("Verification failed: " + (e.message || "Please contact support."));
          })
          .finally(() => setProcessing(false));
      },
      prefill: { name: "", email: "" },
      theme: { color: "#28a745" },
      modal: { ondismiss: () => setProcessing(false) }
    }).open();
  };

  // 5️⃣ Render
  return (
    <div className="checkout-page">
      <div className="checkout-header">
        <h2>Checkout</h2>
        <p>Complete your order securely</p>
      </div>

      <div className="checkout-card">
        {tableInfo.table ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <p style={{ fontSize: '18px', marginBottom: '24px' }}>
              Ordering for Table <strong>{tableInfo.table}</strong> ({tableInfo.type})
            </p>
            <button
              className="btn-primary btn-pay"
              onClick={handlePayAtCounter}
              disabled={processing}
            >
              {processing ? "Processing…" : "Confirm Order"}
            </button>
          </div>
        ) : (
          orderData && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                openRazorpay();
              }}
            >
              <div className="checkout-methods">
                <label className={`method-label ${deliveryData.type === "delivery" ? "active" : ""}`}>
                  <input
                    type="radio" name="otype" value="delivery"
                    checked={deliveryData.type === "delivery"}
                    onChange={e => setDeliveryData(d => ({ ...d, type: e.target.value }))}
                  />
                  <span className="method-icon">🚚</span>
                  Delivery
                </label>
                <label className={`method-label ${deliveryData.type === "pickup" ? "active" : ""}`}>
                  <input
                    type="radio" name="otype" value="pickup"
                    checked={deliveryData.type === "pickup"}
                    onChange={e => setDeliveryData(d => ({ ...d, type: e.target.value }))}
                  />
                  <span className="method-icon">🥡</span>
                  Pickup
                </label>
              </div>

              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  className="form-input"
                  placeholder="Enter your phone number"
                  value={deliveryData.phone}
                  onChange={e => setDeliveryData(d => ({ ...d, phone: e.target.value }))}
                  required
                />
              </div>

              {deliveryData.type === "delivery" && (
                <div className="form-group">
                  <label>Delivery Address</label>
                  <textarea
                    className="form-input"
                    rows="3"
                    placeholder="Enter full delivery address"
                    value={deliveryData.address}
                    onChange={e => setDeliveryData(d => ({ ...d, address: e.target.value }))}
                    required
                  />
                </div>
              )}

              <div className="checkout-summary">
                <div className="summary-row">
                  <span>Subtotal</span>
                  <span>₹{(orderData.amount / 100).toFixed(2)}</span>
                </div>
                <div className="summary-row total">
                  <span>Total to Pay</span>
                  <span>₹{(orderData.amount / 100).toFixed(2)}</span>
                </div>
              </div>

              <button type="submit" className="btn-primary btn-pay" disabled={processing}>
                {processing
                  ? "Processing Payment…"
                  : `Pay securely via Razorpay`}
              </button>
            </form>
          )
        )}
      </div>
    </div>
  );
}
